import React, { useState } from "react";
import TopNav from "@/components/TopNav";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PROFILE } from "@/constants/testIds";
import { toast } from "sonner";

const AVATARS = [
  "https://api.dicebear.com/7.x/adventurer/svg?seed=Ace",
  "https://api.dicebear.com/7.x/adventurer/svg?seed=Nova",
  "https://api.dicebear.com/7.x/adventurer/svg?seed=Kai",
  "https://api.dicebear.com/7.x/adventurer/svg?seed=Zoe",
  "https://api.dicebear.com/7.x/adventurer/svg?seed=Luna",
  "https://api.dicebear.com/7.x/adventurer/svg?seed=Milo",
  "https://api.dicebear.com/7.x/adventurer/svg?seed=Rio",
  "https://api.dicebear.com/7.x/adventurer/svg?seed=Sky",
  "https://api.dicebear.com/7.x/pixel-art/svg?seed=Phoenix",
  "https://api.dicebear.com/7.x/pixel-art/svg?seed=Coco",
  "https://api.dicebear.com/7.x/bottts/svg?seed=Uno",
  "https://api.dicebear.com/7.x/bottts/svg?seed=Rex",
];

export default function Profile() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [avatar, setAvatar] = useState(user?.avatar || user?.picture || AVATARS[0]);
  const [saving, setSaving] = useState(false);

  const authHeader = () => {
    const t = localStorage.getItem("session_token");
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch("/users/me", { name, avatar }, { headers: authHeader() });
      setUser(r.data);
      toast.success("Profile saved");
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen" data-testid={PROFILE.container}>
      <TopNav/>
      <main className="max-w-3xl mx-auto px-6 sm:px-10 py-10 relative z-10">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.3em] text-white/50 mb-2">Profile</div>
          <h1 className="font-display text-4xl tracking-tighter">Your card face.</h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="rounded-2xl bg-[#161324] border border-white/5 p-6 md:col-span-1">
            <div className="flex flex-col items-center">
              <img src={avatar} alt="" className="w-28 h-28 rounded-full border-2 border-white/20"/>
              <div className="mt-4 font-display text-2xl">{name || "Nameless"}</div>
              <div className="text-white/50 text-sm">{user?.email}</div>
              <div className="mt-6 w-full grid grid-cols-4 gap-2 text-center">
                <div><div className="font-display text-xl" style={{color:"var(--uno-green)"}}>{user?.wins || 0}</div><div className="text-[10px] uppercase tracking-widest text-white/50">Wins</div></div>
                <div><div className="font-display text-xl">{user?.losses || 0}</div><div className="text-[10px] uppercase tracking-widest text-white/50">Losses</div></div>
                <div><div className="font-display text-xl" style={{color:"var(--uno-yellow)"}}>{user?.score || 0}</div><div className="text-[10px] uppercase tracking-widest text-white/50">Score</div></div>
                <div><div className="font-display text-xl" style={{color:"var(--uno-red)"}}>{user?.coins || 0}</div><div className="text-[10px] uppercase tracking-widest text-white/50">Coins</div></div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl bg-[#161324] border border-white/5 p-6 md:col-span-2">
            <div className="text-xs uppercase tracking-[0.3em] text-white/50">Display name</div>
            <input
              data-testid={PROFILE.nameInput}
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-2 w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[color:var(--uno-yellow)]"
            />
            <div className="text-xs uppercase tracking-[0.3em] text-white/50 mt-6">Avatar</div>
            <div className="mt-3 grid grid-cols-6 gap-3">
              {AVATARS.map((a, i) => (
                <button
                  key={a}
                  data-testid={`${PROFILE.avatarBtnPrefix}${i}`}
                  onClick={() => setAvatar(a)}
                  className={`rounded-full overflow-hidden border-2 transition-transform hover:scale-105 ${avatar === a ? "border-[color:var(--uno-yellow)]" : "border-white/10"}`}
                >
                  <img src={a} alt="" className="w-14 h-14"/>
                </button>
              ))}
            </div>
            <div className="mt-6">
              <button
                data-testid={PROFILE.saveBtn}
                onClick={save}
                disabled={saving}
                className="px-6 py-2.5 rounded-full bg-[color:var(--uno-red)] text-white font-semibold hover:brightness-110 disabled:opacity-50"
              >Save changes</button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
